// PasteTemplateModal — opened from the SQL editor's right-click "Paste with
// template…" item. The user types a template containing `<RECORD>` (any
// number of times, anywhere), picks quote / blank-line / newline options,
// and the modal pastes the expanded SQL at the cursor.
//
// Why it lives in its own modal (not three menu items like Paste comma list):
// the template is a free-form multi-line string, and a dropdown of recently-
// used templates makes the repetitive case (same UPDATE shape, different IDs
// every day) fast. The expansion logic + persistence are in
// `lib/pastetemplate.js` so they're unit-tested independently.
//
// Props:
//   open               — boolean
//   onClose()          — modal dismiss (Cancel / Esc / backdrop)
//   onPaste(text)      — called with the expanded string when the user clicks
//                        "Paste". Host inserts it into the editor.
import { createSignal, createMemo, createResource, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import {
  expandTemplate, previewExpansion,
  loadPasteTemplates, recordTemplateUse,
} from '../lib/pastetemplate.js';
import { toast } from '../lib/toast.js';

const PREVIEW_ROWS = 5;

// Short label for a saved template in the dropdown — first 70 chars of the
// first non-empty line, with "(+N more lines)" when the template is multi-line.
function dropdownLabel(t) {
  const lines = String(t || '').split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return '(empty)';
  const head = lines[0].length > 70 ? lines[0].slice(0, 70) + '…' : lines[0];
  return lines.length > 1 ? `${head}   (+${lines.length - 1} more line${lines.length === 2 ? '' : 's'})` : head;
}

export default function PasteTemplateModal(props) {
  // Load persisted templates + last-used options. Fires on first open.
  const [stored] = createResource(
    () => (props.open ? Date.now() : null), // re-resolve each time the modal opens
    async () => {
      try { return await loadPasteTemplates(); }
      catch { return { history: [], options: { quote: '', skipBlanks: true, addNewline: true } }; }
    },
  );

  const [template, setTemplate] = createSignal('');
  const [quote, setQuote]       = createSignal(''); // '' | "'" | '"'
  const [skipBlanks, setSkip]   = createSignal(true);
  const [addNewline, setNL]     = createSignal(true);
  const [clipboard, setClipboard] = createSignal('');
  const [reading, setReading]   = createSignal(false);
  // Track whether the user has already typed/edited the template so the
  // stored-options effect doesn't keep overwriting their input mid-session.
  let appliedStored = false;
  const ensureStoredApplied = () => {
    if (appliedStored) return;
    const s = stored();
    if (!s) return;
    appliedStored = true;
    setQuote(s.options?.quote || '');
    setSkip(s.options?.skipBlanks !== false);
    setNL(s.options?.addNewline !== false);
    if (s.history?.length && !template()) {
      setTemplate(s.history[0].template);
    }
  };

  // Read the clipboard once on open so the preview reflects what'll actually
  // be pasted. The user can Refresh to re-read after switching contexts.
  const readClipboard = async () => {
    setReading(true);
    try {
      if (navigator.clipboard?.readText) {
        const raw = await navigator.clipboard.readText();
        setClipboard(raw || '');
      } else {
        setClipboard('');
        toast.error('Clipboard read needs HTTPS or localhost.');
      }
    } catch (e) {
      toast.error('Clipboard read denied: ' + (e?.message || e));
      setClipboard('');
    } finally {
      setReading(false);
    }
  };

  // First-open setup: apply saved options/template + read the clipboard.
  let openedOnce = false;
  const onModalReady = () => {
    if (!props.open || openedOnce) return;
    openedOnce = true;
    appliedStored = false;
    ensureStoredApplied();
    readClipboard();
  };

  // Re-trigger setup whenever `open` flips true. Run inside the JSX via a
  // <Show>'s `when` — simplest reactive entry point.
  const handleOpen = createMemo(() => {
    if (!props.open) {
      openedOnce = false;
      return false;
    }
    // Schedule setup on the next tick so the resource has a chance to load.
    queueMicrotask(onModalReady);
    return true;
  });

  // Re-apply stored options once the resource resolves (after the first open).
  createMemo(() => { if (stored() && props.open) ensureStoredApplied(); });

  const opts = () => ({ quote: quote(), skipBlanks: skipBlanks(), addNewline: addNewline() });
  const preview = createMemo(() => previewExpansion(template(), clipboard(), opts(), PREVIEW_ROWS));

  const recordCount = createMemo(() => {
    if (!clipboard()) return 0;
    const lines = clipboard().split(/\r?\n/);
    return skipBlanks() ? lines.filter((l) => l.trim().length > 0).length : lines.length;
  });

  const canPaste = () => recordCount() > 0 && template().length > 0;

  const pickFromHistory = (e) => {
    const idx = Number(e.currentTarget.value);
    const h = stored()?.history?.[idx];
    if (h) setTemplate(h.template);
  };

  const onSubmit = async () => {
    const expanded = expandTemplate(template(), clipboard(), opts());
    if (!expanded) {
      toast.error('Nothing to paste — clipboard is empty.');
      return;
    }
    // Persist BEFORE the host inserts so a slow network call doesn't delay paste.
    recordTemplateUse(template(), opts()).catch(() => {});
    props.onPaste?.(expanded);
    props.onClose?.();
  };

  return (
    <Show when={handleOpen()}>
      <Modal open={true} title="Paste with template" onClose={props.onClose} wide={true}>
        <div class="paste-tpl">
          <p class="muted" style="margin:0;font-size:12px;">
            The clipboard's lines become records. Each <span class="mono">&lt;RECORD&gt;</span> in
            the template is replaced with the record value (every occurrence, every line).
          </p>

          {/* Recent templates — dropdown drops the picked one into the textarea
              below. Hidden when no history yet. */}
          <Show when={stored()?.history?.length}>
            <label class="paste-tpl-field">
              <span class="paste-tpl-label">Recent templates</span>
              <select class="input" onChange={pickFromHistory}>
                <option value="" disabled selected>Pick a previous template…</option>
                <For each={stored().history}>
                  {(h, i) => <option value={i()}>{dropdownLabel(h.template)}</option>}
                </For>
              </select>
            </label>
          </Show>

          <label class="paste-tpl-field">
            <span class="paste-tpl-label">Template <span class="muted">(use <span class="mono">&lt;RECORD&gt;</span> as the placeholder)</span></span>
            <textarea
              class="input mono"
              rows="4"
              spellcheck="false"
              value={template()}
              onInput={(e) => setTemplate(e.currentTarget.value)}
              placeholder="UPDATE tr_jual_m SET Sisa=0 WHERE NoJual = <RECORD>;"
            />
          </label>

          <div class="paste-tpl-opts">
            <div class="paste-tpl-radiogroup">
              <span class="paste-tpl-label">Quote each record</span>
              <label class="paste-tpl-radio">
                <input type="radio" name="ptq" checked={quote() === ''}   onChange={() => setQuote('')} />
                <span>None</span>
              </label>
              <label class="paste-tpl-radio">
                <input type="radio" name="ptq" checked={quote() === "'"} onChange={() => setQuote("'")} />
                <span class="mono">'single'</span>
              </label>
              <label class="paste-tpl-radio">
                <input type="radio" name="ptq" checked={quote() === '"'} onChange={() => setQuote('"')} />
                <span class="mono">"double"</span>
              </label>
            </div>

            <div class="paste-tpl-checks">
              <label class="paste-tpl-check">
                <input type="checkbox" checked={skipBlanks()} onChange={(e) => setSkip(e.currentTarget.checked)} />
                <span>Skip blank lines in the clipboard</span>
              </label>
              <label class="paste-tpl-check">
                <input type="checkbox" checked={addNewline()} onChange={(e) => setNL(e.currentTarget.checked)} />
                <span>Add a newline between records</span>
              </label>
            </div>
          </div>

          {/* Live preview — first N expanded records. Tells the user the
              quoting / newline choice produced what they expected before they
              commit to dropping it into the editor. */}
          <div class="paste-tpl-preview">
            <div class="paste-tpl-preview-head">
              <span class="paste-tpl-label">Preview</span>
              <span class="muted" style="font-size:11px;">
                {clipboard()
                  ? <>{recordCount()} record{recordCount() === 1 ? '' : 's'} from clipboard</>
                  : 'clipboard is empty'}
              </span>
              <span class="spacer" />
              <button class="btn btn-sm btn-ghost" onClick={readClipboard} disabled={reading()}>
                {reading() ? '⏳ Reading…' : '⟳ Re-read clipboard'}
              </button>
            </div>
            <pre class="paste-tpl-preview-body mono">{preview().text || '(empty)'}</pre>
            <Show when={preview().truncatedFrom}>
              <div class="muted" style="font-size:11px;margin-top:4px;">
                Preview clipped to {PREVIEW_ROWS} of {preview().truncatedFrom} records.
              </div>
            </Show>
          </div>

          <div class="paste-tpl-actions">
            <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
            <button class="btn btn-primary" onClick={onSubmit} disabled={!canPaste()}>
              ⬇ Paste {recordCount() > 0 ? `${recordCount()} record${recordCount() === 1 ? '' : 's'}` : ''}
            </button>
          </div>
        </div>
      </Modal>
    </Show>
  );
}
